import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowRight, Lock, User, Eye, EyeOff, Smartphone, ShieldCheck, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobalSettings } from '@/hooks/useGlobalSettings';
import logo from '@/assets/logo.png';
import { Button } from '@/components/ui/button';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';

// Top 50 Countries for expansion
const COUNTRIES = [
  { code: '+91', label: '🇮🇳 India', min: 10, max: 10 },
  { code: '+1', label: '🇺🇸 USA', min: 10, max: 10 },
  { code: '+44', label: '🇬🇧 UK', min: 10, max: 10 },
  { code: '+971', label: '🇦🇪 UAE', min: 9, max: 9 },
  { code: '+65', label: '🇸🇬 Singapore', min: 8, max: 8 },
  { code: '+61', label: '🇦🇺 Australia', min: 9, max: 9 },
  { code: '+1', label: '🇨🇦 Canada', min: 10, max: 10 },
  { code: '+49', label: '🇩🇪 Germany', min: 10, max: 11 },
  { code: '+33', label: '🇫🇷 France', min: 9, max: 9 },
  { code: '+966', label: '🇸🇦 Saudi Arabia', min: 9, max: 9 },
].sort((a, b) => (a.code === '+91' ? -1 : b.code === '+91' ? 1 : a.label.localeCompare(b.label)));

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  const { settings } = useGlobalSettings();
  const isPhoneEnabled = settings?.phone_auth_enabled ?? true;
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [showOtp, setShowOtp] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  const [phoneAuthError, setPhoneAuthError] = useState(false);

  // 🔒 Security: Firebase auth state stored in component refs instead of window globals
  // Prevents exposure to malicious browser extensions or XSS attacks
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recaptchaRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const confirmationRef = useRef<any>(null);

  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);




  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) interval = setInterval(() => setResendTimer((r) => r - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Handle deep-link callback from Google OAuth on Android (in-app Custom Tabs)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listener = App.addListener('appUrlOpen', async ({ url }) => {
      if (url.startsWith('com.fixeveryday.app://callback')) {
        // Close the in-app browser
        await Browser.close();

        // Extract token/code from the URL and establish session
        const params = new URLSearchParams(url.split('?')[1] || url.split('#')[1] || '');
        const access_token = params.get('access_token');
        const refresh_token = params.get('refresh_token');
        const error_description = params.get('error_description');

        if (error_description) {
          sessionStorage.removeItem('is_logging_in');
          toast({ title: 'Google sign-in failed', description: error_description, variant: 'destructive' });
          setGoogleLoading(false);
          return;
        }

        if (access_token) {
          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token: refresh_token || '',
          });
          if (error) {
            sessionStorage.removeItem('is_logging_in');
            toast({ title: 'Session error', description: error.message, variant: 'destructive' });
            setGoogleLoading(false);
          }
          // AuthContext will detect user and navigate
        }
      }
    });

    return () => { listener.then(l => l.remove()); };
  }, [toast]);



  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    sessionStorage.setItem('is_logging_in', 'true');
    try {
      if (Capacitor.isNativePlatform()) {
        const googleUser = await GoogleAuth.signIn();
        if (googleUser.authentication.idToken) {
          const { error } = await supabase.auth.signInWithIdToken({
            provider: 'google',
            token: googleUser.authentication.idToken,
          });
          if (error) throw error;
        } else {
           throw new Error('No ID token from Google');
        }
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.origin,
          },
        });
        if (error) throw error;
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Google Sign-In Error:', error);
      sessionStorage.removeItem('is_logging_in');
      toast({ title: 'Google sign-in failed', description: 'Authentication failed. Please try again.', variant: 'destructive' });
      setGoogleLoading(false);
    }
  };

  const handleSendOTP = async () => {
    if (!phone || phone.length < 8) {
      toast({ title: 'Invalid Phone', description: 'Please enter a valid mobile number', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const { RecaptchaVerifier, signInWithPhoneNumber } = await import('firebase/auth');
      const { getFirebaseAuth } = await import('@/lib/firebase');
      const auth = await getFirebaseAuth();
      
      if (!recaptchaRef.current) {
        recaptchaRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible'
        });
      }

      const verifier = recaptchaRef.current;
      const fullPhone = `${countryCode}${phone}`;
      
      const confirmation = await signInWithPhoneNumber(auth, fullPhone, verifier);
      confirmationRef.current = confirmation;
      
      setOtpStep(true);
      setResendTimer(90);
      toast({ title: 'OTP Sent! 📱', description: `Code sent to ${fullPhone}` });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('SMS Send Error:', error);
      setPhoneAuthError(true);
      toast({ 
        title: 'Phone verification not available', 
        description: 'Please try Google Authentication or Email login.', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length < 6) {
      toast({ title: 'Invalid OTP', description: 'Please enter the 6-digit code', variant: 'destructive' });
      return;
    }

    const confirmation = confirmationRef.current;
    if (!confirmation) {
      toast({ title: 'Session Expired', description: 'Please request a new OTP.', variant: 'destructive' });
      setOtpStep(false);
      return;
    }

    setLoading(true);
    try {
      const result = await confirmation.confirm(otp);
      const firebaseUser = result.user;
      
      if (firebaseUser) {
        sessionStorage.setItem('is_logging_in', 'true');
        const idToken = await firebaseUser.getIdToken();
        
        const { data, error } = await supabase.functions.invoke('firebase-auth-bridge', {
          body: { idToken, phone: firebaseUser.phoneNumber }
        });

        if (error) throw error;

        if (data?.token_hash) {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: data.token_hash,
            type: data.type || 'magiclink'
          });
          if (verifyError) throw verifyError;
          navigate('/', { replace: true });
        }
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Verification Error:', error);
      toast({ 
        title: 'Verification Failed', 
        description: 'The code is invalid or expired. Please try Google login.', 
        variant: 'destructive' 
      });
      setPhoneAuthError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async () => {
    setLoading(true);
    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else if (mode === 'signup') {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { display_name: name } }
        });
        if (error) throw error;
        toast({ title: 'Success!', description: 'Please check your email to verify your account.' });
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(email);
        if (error) throw error;
        toast({ title: 'Email Sent', description: 'Check your inbox for the reset link.' });
      }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast({ title: 'Error', description: 'Invalid credentials. Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen gradient-mesh flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <img src={logo} alt="Logo" className="h-20 w-20 rounded-full mx-auto mb-4 object-cover ring-2 ring-primary/20 shadow-lg" />
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#FF0000] italic tracking-tight">FixEveryday</h1>
          <p className="text-sm text-muted-foreground mt-1 font-medium">Home services at your doorstep</p>
        </div>

        <div className="bg-card rounded-3xl shadow-elevated border border-border/50 p-6 md:p-8 backdrop-blur-xl bg-card/90">
          <AnimatePresence mode="wait">
            {!showEmailForm && !phoneAuthError && isPhoneEnabled ? (
              <motion.div key="phone" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }}>
                {!otpStep ? (
                  <div className="space-y-4">
                    <div className="flex rounded-2xl bg-secondary border border-border/50 focus-within:ring-2 focus-within:ring-primary/20 transition-all overflow-hidden">
                      <select 
                        className="bg-transparent text-sm font-bold pl-4 pr-2 border-r border-border/50 focus:outline-none cursor-pointer"
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                      >
                        {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                      </select>
                      <input
                        type="tel"
                        value={phone}
                        onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="Mobile Number"
                        className="w-full h-12 px-4 bg-transparent text-sm font-bold placeholder:text-muted-foreground/40 focus:outline-none"
                      />
                    </div>
                    
                    <Button 
                      onClick={handleSendOTP} 
                      disabled={loading || phone.length < 10}
                      className="w-full h-12 rounded-2xl bg-[#FF0000] hover:bg-[#CC0000] text-white font-bold text-sm shadow-lg shadow-red-500/20"
                    >
                      {loading ? "Sending..." : "Get OTP"}
                    </Button>

                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 h-px bg-border/60" />
                      <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest leading-none">or</span>
                      <div className="flex-1 h-px bg-border/60" />
                    </div>

                    <Button 
                      variant="outline" 
                      onClick={handleGoogleSignIn}
                      disabled={googleLoading}
                      className="w-full h-12 rounded-2xl bg-secondary border border-border/50 font-semibold text-sm gap-3 hover:bg-accent"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                      Continue with Google
                    </Button>

                    <button onClick={() => setShowEmailForm(true)} className="w-full mt-2 text-xs text-muted-foreground hover:text-foreground text-center font-bold">
                      Use email instead
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="font-extrabold text-xl">Confirm OTP</h3>
                      <p className="text-sm text-muted-foreground">Code sent to {countryCode} {phone}</p>
                    </div>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="w-full h-14 rounded-2xl bg-secondary border border-border/50 text-center text-2xl font-black tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <Button 
                      onClick={handleVerifyOTP} 
                      disabled={loading || otp.length !== 6}
                      className="w-full h-12 rounded-2xl bg-[#FF0000] hover:bg-[#CC0000] text-white font-bold"
                    >
                      {loading ? "Verifying..." : "Continue"}
                    </Button>
                    <button 
                      disabled={resendTimer > 0} 
                      onClick={handleSendOTP}
                      className={`w-full text-xs font-bold ${resendTimer > 0 ? 'text-muted-foreground' : 'text-primary'}`}
                    >
                      {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                    </button>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div key="fallback" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>


                <Button 
                  variant="outline" 
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading}
                  className="w-full h-14 rounded-2xl bg-secondary border border-border/50 font-bold text-base gap-3 shadow-md"
                >
                  <svg className="h-6 w-6" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                  Continue with Google
                </Button>

                <div className="flex items-center gap-3 my-6">
                  <div className="flex-1 h-px bg-border/60" />
                  <span className="text-[10px] text-muted-foreground font-black uppercase tracking-widest leading-none">or email</span>
                  <div className="flex-1 h-px bg-border/60" />
                </div>

                <div className="space-y-4">
                  {mode === 'signup' && (
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Full Name"
                      className="w-full h-12 px-4 rounded-xl bg-secondary border border-border/50 text-sm font-bold focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  )}
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Email Address"
                    className="w-full h-12 px-4 rounded-xl bg-secondary border border-border/50 text-sm font-bold focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                  {mode !== 'forgot' && (
                    <input
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Password"
                      className="w-full h-12 px-4 rounded-xl bg-secondary border border-border/50 text-sm font-bold focus:ring-2 focus:ring-primary/20 transition-all"
                    />
                  )}
                  <Button onClick={handleEmailAuth} disabled={loading} className="w-full h-12 rounded-xl bg-foreground text-background font-black uppercase text-xs tracking-widest shadow-lg">
                    {loading ? "Processing..." : (mode === 'login' ? 'Sign In' : mode === 'forgot' ? 'Reset Link' : 'Create Account')}
                  </Button>
                </div>

                <div className="flex justify-between items-center mt-4">
                  <button onClick={() => setMode(mode === 'login' ? 'signup' : 'login')} className="text-xs text-muted-foreground hover:text-foreground font-bold">
                    {mode === 'login' ? "Create Account" : "Back to Sign In"}
                  </button>
                  {isPhoneEnabled && !phoneAuthError && (
                    <button onClick={() => setShowEmailForm(false)} className="text-xs text-primary font-bold hover:underline">
                      Phone Login
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="text-center text-[10px] text-muted-foreground mt-8 font-bold opacity-60">
          By continuing, you agree to our <a href="/terms" className="underline">Terms</a> & <a href="/terms" className="underline">Privacy Policy</a>
        </p>

        <div id="recaptcha-container" className="hidden"></div>
      </motion.div>
    </div>
  );
};

export default Login;
